import { advance, dayOf, earn, message, money, type LifeEvent, type LifeState } from './life.ts';
import type { Job } from './content/types.ts';
import { COUNTRY_NAME, USD_NGN, homeCountry } from './travel.ts';
import { socialOf } from './social.ts';
import { Rng } from './rng.ts';

/**
 * The astronaut: a job like any other to get (a degree in a science, the selection exams, fit enough, and a checklist
 * that says so), then training shifts at the Astronaut Corps' centre in your city (a full-size station mockup), until
 * Flight Operations puts you on a crew. Launch day is a date in your diary; after it you live on the International
 * Space Station and you can't come home until the crew does: six days of a timeline (an experiment, a repair, a
 * spacewalk, Earth from the cupola, a drill), eating at the galley, sleeping in a bag on the wall. Then the capsule
 * brings you home. Most of the work is the ordinary career (training shifts, reviews, promotions); the missions are the
 * part you play.
 */

const DAY = 1440;

/** The job (the Corps hires in every country: its centre is in your city). Pay is dollars, the same everywhere. */
export const ASTRONAUT: Job = {
  id: 'astronaut', label: 'Astronaut candidate', place: ['the International Astronaut Corps training centre'],
  income: [7_500 * USD_NGN, 11_000 * USD_NGN], stages: ['adult'], minEdu: 'degree', fields: ['engineering', 'computing', 'medicine'], weight: 0,
  skills: { academics: 40, fitness: 35, technical: 25 },
};
export const isAstronaut = (s: LifeState) => s.job?.id === 'astronaut';

/** Training shifts before your first crew assignment (then fewer between missions). */
export const TRAIN_FIRST = 3, TRAIN_AGAIN = 5;
/** Days on the station (launch day is day 0; the capsule leaves on the last). */
export const MISSION_DAYS = 6;

export type OrbitTask = 'experiment' | 'repair' | 'eva' | 'observe' | 'drill';
/** The timeline: what each day on the station is for (day 0 is arrival; the last day is going home). */
export const TIMELINE: (OrbitTask | null)[] = [null, 'experiment', 'repair', 'eva', 'observe', 'drill', null];
export const TASK_LABEL: Record<OrbitTask, string> = {
  experiment: 'Microgravity experiment (the lab)', repair: 'Oxygen system maintenance', eva: 'Spacewalk: replace a failed power unit on the truss',
  observe: 'Earth observation from the cupola', drill: 'Emergency drill',
};

export interface Crewmate { name: string; from: string; role: string }
export interface SpaceState {
  stage: 'none' | 'assigned' | 'orbit';
  /** training shifts since the last mission (or since you were hired) */
  trained: number;
  missions: number;
  expedition: number;
  /** the launch (minutes), and the day the capsule brings you home */
  launchAt: number;
  returnAt: number;
  crew: Crewmate[];
  /** the timeline tasks done this mission (and how well) */
  done: Partial<Record<OrbitTask, number>>;
  evas: number;
  photos: number;
  /** launches missed (the seat goes to the backup) */
  scrubbed: number;
}

export function spaceOf(s: LifeState): SpaceState {
  return (s.space ??= { stage: 'none', trained: 0, missions: 0, expedition: 0, launchAt: 0, returnAt: 0, crew: [], done: {}, evas: 0, photos: 0, scrubbed: 0 });
}
export const inOrbit = (s: LifeState) => s.space?.stage === 'orbit';
/** The day of the mission (0 = launch day), or -1. */
export const missionDay = (s: LifeState) => (inOrbit(s) ? dayOf(s.minutes) - dayOf(s.space!.launchAt) : -1);
/** Today's timeline task on the station (null on arrival and departure days). */
export function todaysTask(s: LifeState): OrbitTask | null {
  const d = missionDay(s);
  return d >= 0 ? TIMELINE[Math.min(d, TIMELINE.length - 1)] : null;
}
/** Going home: today, and the capsule is waiting (from 09:00). */
export const homeTime = (s: LifeState) => inOrbit(s) && s.minutes >= s.space!.returnAt;
/** On the station, the timeline is the shift: every day, 08:00 to 17:00. */
export const ORBIT_SCHEDULE = { days: [0, 1, 2, 3, 4, 5, 6], start: 8, end: 17, pay: 'monthly' as const };
/** Launch day, and the hours the crew can still make it to the pad (from 04:00 until the launch). */
export function launchToday(s: LifeState): boolean {
  const sp = s.space;
  return !!sp && sp.stage === 'assigned' && dayOf(s.minutes) === dayOf(sp.launchAt) && s.minutes >= sp.launchAt - 5 * 60 && s.minutes < sp.launchAt;
}

const CREW_POOL: Crewmate[] = [
  { name: 'Mikhail Orlov', from: 'Russia', role: 'Commander' }, { name: 'Dana Whitfield', from: 'the United States', role: 'Commander' },
  { name: 'Kenji Arakawa', from: 'Japan', role: 'Flight engineer' }, { name: 'Léa Martel', from: 'France', role: 'Flight engineer' },
  { name: 'Priya Raman', from: 'India', role: 'Flight engineer' }, { name: 'Tomás Rivera', from: 'Spain', role: 'Flight engineer' },
  { name: 'Ingrid Solberg', from: 'Norway', role: 'Flight engineer' }, { name: 'Chen Haoyu', from: 'China', role: 'Flight engineer' },
  { name: 'Amara Okafor', from: 'Nigeria', role: 'Flight surgeon' }, { name: 'Marcus Webb', from: 'Canada', role: 'Flight engineer' },
];

/** After a training shift: enough of them, and Flight Operations puts you on the next crew. */
export function afterTraining(s: LifeState, events: LifeEvent[]) {
  if (!isAstronaut(s)) return;
  const sp = spaceOf(s);
  if (sp.stage !== 'none') return;
  sp.trained++;
  if (sp.trained < (sp.missions ? TRAIN_AGAIN : TRAIN_FIRST)) return;
  const r = new Rng((s.seed ^ Math.imul(sp.missions + 7, 0x51ed27)) >>> 0);
  const launchDay = dayOf(s.minutes) + 3;
  sp.stage = 'assigned';
  sp.expedition = 74 + sp.missions * 2 + r.int(0, 1);
  sp.launchAt = launchDay * DAY + 9 * 60;
  sp.returnAt = (launchDay + MISSION_DAYS) * DAY + 9 * 60;
  sp.done = {};
  const cmd = r.pick(CREW_POOL.filter((c) => c.role === 'Commander')), rest = r.sample(CREW_POOL.filter((c) => c.role !== 'Commander' && c.from !== COUNTRY_NAME[homeCountry(s)]), 2);
  sp.crew = [cmd, ...rest];
  message(s, 'Flight Operations', `You're on the crew: Expedition ${sp.expedition}, launching in three days at 09:00 (be at the launch site from 04:00: Where to? → the launch pad). ${MISSION_DAYS} days on the station with ${sp.crew.map((c) => `${c.name} (${c.from})`).join(', ')}. Once you're up there, you're up there until the capsule comes home.`, events);
  s.history.push({ t: s.minutes, text: `Assigned to Expedition ${sp.expedition}.` });
}

/** A missed launch: the backup flies; you go back to training (once is forgiven, twice and you're off the corps). */
export function spaceDay(s: LifeState, events: LifeEvent[]) {
  const sp = s.space;
  if (!sp) return;
  if (sp.stage === 'assigned' && s.minutes > sp.launchAt + 60) {
    sp.stage = 'none'; sp.trained = 0; sp.scrubbed++;
    message(s, 'Flight Operations', sp.scrubbed >= 2 ? 'You missed a second launch. The Corps has let you go.' : 'You weren\'t at the pad. Your backup flew in your seat. Back to training: we\'ll talk about the next crew.', events);
    if (sp.scrubbed >= 2 && isAstronaut(s)) { s.history.push({ t: s.minutes, text: 'Let go by the Astronaut Corps.' }); s.job = null; }
  }
}

/** Lift-off: the hours to orbit and the docking pass in the capsule; you're on the station. */
export function launch(s: LifeState): LifeEvent[] {
  const sp = spaceOf(s);
  if (sp.stage !== 'assigned') return [];
  sp.stage = 'orbit';
  sp.done = {};
  const wait = Math.max(0, sp.launchAt - s.minutes);
  const events = advance(s, wait + 6 * 60, { busy: true }); // (the countdown, then about six hours to catch the station)
  s.needs.fun = Math.min(100, s.needs.fun + 40);
  s.history.push({ t: s.minutes, text: `Launched to the International Space Station (Expedition ${sp.expedition}).` });
  socialOf(s).followers += 1_500 + sp.missions * 500;
  for (const c of sp.crew) if (!s.contacts.some((p) => p.name === c.name)) s.contacts.push({ name: c.name, relation: 'Crewmate', closeness: 40, met: s.minutes, seen: s.minutes });
  return events;
}

/** A day's task on the timeline, done (performance 0..1): it goes in the mission's record. */
export function orbitTaskDone(s: LifeState, task: OrbitTask, performance: number): string {
  const sp = spaceOf(s);
  sp.done[task] = Math.max(sp.done[task] ?? 0, performance);
  if (task === 'eva') { sp.evas++; s.history.push({ t: s.minutes, text: `Spacewalk${sp.evas > 1 ? ` (number ${sp.evas})` : ''}: ${Math.round(performance * 100)}% of the job done outside.` }); socialOf(s).followers += 2_500; }
  if (task === 'observe') sp.photos += Math.round(performance * 6);
  s.skills.technical = Math.min(100, s.skills.technical + 0.8 * performance);
  s.skills.academics = Math.min(100, s.skills.academics + 0.5 * performance);
  for (const c of s.contacts.filter((p) => p.relation === 'Crewmate')) { c.closeness = Math.min(100, c.closeness + 3); c.seen = s.minutes; }
  return performance > 0.8 ? 'Houston (well, the control centre) is delighted.' : performance > 0.5 ? 'Done, with a couple of calls to the ground.' : 'Done, just. The ground team will be going over it.';
}

/** Splashdown: home, a mission bonus, and back to training for the next one. */
export function landHome(s: LifeState): LifeEvent[] {
  const sp = spaceOf(s);
  if (sp.stage !== 'orbit') return [];
  const events: LifeEvent[] = [];
  sp.stage = 'none'; sp.trained = 0; sp.missions++;
  const tasks = Object.values(sp.done) as number[], score = tasks.length ? tasks.reduce((a, b) => a + b, 0) / tasks.length : 0.4;
  const bonus = Math.round((4_000 + 6_000 * score) * USD_NGN / 1000) * 1000;
  earn(s, bonus, `Mission allowance · Expedition ${sp.expedition}`);
  events.push({ t: s.minutes, kind: 'pay', text: `Mission allowance: ${money(s, bonus)}`, amount: bonus });
  s.work.performance = Math.min(100, s.work.performance + 10 * score);
  // the first flight makes you an astronaut (a candidate no longer)
  if (s.job && !s.job.rank) { s.job.baseLabel ??= s.job.label; s.job.rank = 1; s.job.label = 'Astronaut'; s.job.promotedAt = s.work.worked; s.job.incomeMonthly = Math.round(s.job.incomeMonthly * 1.2 / 1000) * 1000; }
  s.history.push({ t: s.minutes, text: `Came home from the space station: ${MISSION_DAYS} days, ${sp.missions === 1 ? 'the first flight' : `flight number ${sp.missions}`}.` });
  socialOf(s).followers += 3_000;
  s.needs.social = Math.min(100, s.needs.social + 30);
  s.needs.energy = Math.max(10, s.needs.energy - 30); // (gravity again: everything is heavy for a day or two)
  return events;
}

/** The Jobs listing's extra requirements for the Corps (the selection exams, the gym). */
export const ASTRONAUT_NEEDS = [{ k: 'age' as const, min: 18, max: 46 } /* (nobody ages: 23 could never come for an 18-year-old; the degree is the gate) */, { k: 'edu' as const, level: 'degree' as const }, { k: 'field' as const, fields: ['engineering', 'computing', 'medicine'] },
  { k: 'skill' as const, skill: 'academics' as const, min: 40 }, { k: 'skill' as const, skill: 'fitness' as const, min: 30 }, { k: 'gym' as const, n: 6 }, { k: 'cert' as const, id: 'astro' }];

/** Something to say about where you are, for the phone and the HUD. */
export function missionLine(s: LifeState): string | null {
  const sp = s.space;
  if (!sp || sp.stage === 'none') return null;
  if (sp.stage === 'assigned') return `Expedition ${sp.expedition}: launch ${dayOf(sp.launchAt) === dayOf(s.minutes) ? 'today' : `in ${dayOf(sp.launchAt) - dayOf(s.minutes)} day${dayOf(sp.launchAt) - dayOf(s.minutes) === 1 ? '' : 's'}`} at 09:00`;
  const left = dayOf(sp.returnAt) - dayOf(s.minutes);
  return `Expedition ${sp.expedition} · day ${missionDay(s)} of ${MISSION_DAYS} · ${left > 0 ? `home in ${left} day${left === 1 ? '' : 's'}` : 'going home today'}`;
}
